/**
 * The "Sign in" button at the top right of every page, and what it says once
 * you have.
 *
 * The button itself is markup — `accountHtml` in `nav.mjs`, written into the
 * pre-rendered pages at build time and spelt out in the masthead of the rest — so
 * it is a working link to `account/` with no script at all. This changes its
 * face — your initial once signed in — and makes pressing it open a menu, for
 * everybody: Sign in, signed out; Account, Admin for an admin, and Sign out, in.
 *
 * The menu also takes over the page's own Install, View and theme buttons
 * (`#install`, `#view-open`, `#theme`): they leave the masthead, which `nav.css`
 * does off `data-account-menu` on `<html>`, and come back as items here that
 * press them. The buttons stay in the page and keep all their wiring; with no
 * script there is no menu, and they are where they always were.
 *
 * ASKING THE SERVER ONLY WHEN THERE IS SOMEBODY TO ASK ABOUT
 * The session cookie is HttpOnly, so a page cannot see whether it has one. The
 * obvious fix — ask `account/api/me` on every page load — is a Worker request and
 * a database read for every visitor to every page, nearly all of whom have never
 * signed in. So the account page leaves a note in `localStorage` when somebody
 * signs in and removes it when they sign out, and this asks only when the note is
 * there. A note that has outlived its session (it expired, or a reset ended it)
 * is found out on the next page and removed.
 *
 * Loaded by every page with a masthead, from the site root, so the API address is
 * resolved against this file and is right under `npm run dev` and deployed alike.
 */

import { SIGNED_IN_KEY } from './site.mjs';
import { SUN, MOON } from './theme.js';

const KEY = SIGNED_IN_KEY;
const ME = new URL('./account/api/me', import.meta.url);
const LOGOUT = new URL('./account/api/logout', import.meta.url);
const ADMIN = new URL('./admin/', import.meta.url);
const PROFILE = new URL('./profile/', import.meta.url);
const MY_GAMES = new URL('./my-games/', import.meta.url);
const LEADERBOARD = new URL('./leaderboard/', import.meta.url);
const ACCOUNT = new URL('./account/', import.meta.url);
/** A player's public page, by name. */
export const playerUrl = (name) => new URL(`./@/${encodeURIComponent(name)}`, import.meta.url).href;
/** The key `analytics.js` and `game-stats.js` read before reporting anything. */
const ANALYTICS_OFF_KEY = 'immortal-analytics-off';

/** Called by the account page on sign-in and sign-out. */
export function noteSignedIn(on) {
  try {
    if (on) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch { /* storage blocked: the button just keeps saying "Sign in" */ }
}

/** Who to say is signed in: the username, else the address, else the Lichess name. */
const labelOf = (user) => user.username || user.email || `${user.lichess} on Lichess`;

/**
 * The signed-in user as `account/api/me` gives it, or null — for a page that
 * would fill something in for them (the library's import, with their Lichess name).
 * Null straight away for anybody this browser has not seen sign in.
 */
let settle;
export const accountUser = new Promise((resolve) => { settle = resolve; });

function signedInFace(link, email) {
  const initial = (email.match(/[a-z0-9]/i)?.[0] || '?').toUpperCase();
  const badge = document.createElement('span');
  badge.className = 'nav-login-badge';
  badge.setAttribute('aria-hidden', 'true');
  badge.textContent = initial;
  link.querySelector('svg')?.replaceWith(badge);
  const label = link.querySelector('.nav-login-label');
  if (label) label.textContent = 'Account';
  link.classList.add('in');
  link.setAttribute('aria-label', `Account — signed in as ${email}`);
  link.title = `Signed in as ${email}`;
}

/*
 * THE MENU, once signed in. Laid over the page with `position: fixed` and appended
 * to `<body>` rather than put beside the link, because the link sits in a dozen
 * mastheads with rules of their own (`.tools + .nav-login`, `data-login-wrap`) and
 * a wrapper round it would break every one of them. With no script the link is
 * still just a link to the account page.
 */
let panel = null;
let opener = null;

/** 16px line icons for the menu, drawn like the rest of the masthead's. */
const svg = (paths) => '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"'
  + ` stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
const ICONS = {
  profile: '<circle cx="8" cy="5.6" r="2.6"></circle><path d="M3 13.4c.6-2.4 2.6-3.8 5-3.8s4.4 1.4 5 3.8"></path>',
  page: '<circle cx="8" cy="8" r="6"></circle><path d="M2 8h12M8 2c1.8 1.7 2.6 3.7 2.6 6S9.8 12.3 8 14'
    + 'M8 2C6.2 3.7 5.4 5.7 5.4 8s.8 4.3 2.6 6"></path>',
  games: '<rect x="2.5" y="2.5" width="11" height="11" rx="1.2"></rect><path d="M2.5 8h11M8 2.5v11"></path>'
    + '<path d="M2.5 2.5h5.5v5.5H2.5zM8 8h5.5v5.5H8z" fill="currentColor" stroke="none" opacity=".35"></path>',
  board: '<path d="M5 2.5h6v3.2a3 3 0 01-6 0z"></path><path d="M5 3.8H3v.9a2 2 0 002 2M11 3.8h2v.9a2 2 0 01-2 2'
    + 'M8 8.7v2.6M5.5 13.5h5"></path>',
  account: '<rect x="1.8" y="3.2" width="12.4" height="9.6" rx="1.4"></rect><circle cx="5.6" cy="7.2" r="1.4"></circle>'
    + '<path d="M3.6 10.6c.4-1 1.1-1.5 2-1.5s1.6.5 2 1.5M9.6 6.6h2.6M9.6 9.2h2.6"></path>',
  admin: '<path d="M8 1.8l5 1.9v4c0 3.1-2.1 5.4-5 6.5-2.9-1.1-5-3.4-5-6.5v-4z"></path><path d="M5.8 8l1.6 1.6 3-3.2"></path>',
  view: '<path d="M2 4.5h4M9.5 4.5H14M2 11.5h6M11.5 11.5H14"></path>'
    + '<circle cx="8" cy="4.5" r="1.7"></circle><circle cx="10" cy="11.5" r="1.7"></circle>',
  install: '<path d="M8 2.4v7.2M5.2 6.8 8 9.6l2.8-2.8"></path>'
    + '<path d="M2.8 10.4v2.2c0 .5.4.9.9.9h8.6c.5 0 .9-.4.9-.9v-2.2"></path>',
  signOut: '<path d="M6.5 2.5H4a1.5 1.5 0 00-1.5 1.5v8A1.5 1.5 0 004 13.5h2.5M10 5l3 3-3 3M13 8H6.5"></path>',
};

/** Hide the masthead's Install, View and theme buttons for the menu to hold — see the top of this file. */
function takeTools(on) {
  if (on) document.documentElement.setAttribute('data-account-menu', '');
  else document.documentElement.removeAttribute('data-account-menu');
}

function closeMenu({ focus = false } = {}) {
  if (!panel || panel.hidden) return;
  panel.hidden = true;
  opener?.setAttribute('aria-expanded', 'false');
  if (focus) opener?.focus();
}

function place() {
  const box = opener.getBoundingClientRect();
  panel.style.top = `${Math.round(box.bottom + 6)}px`;
  panel.style.right = `${Math.max(8, Math.round(document.documentElement.clientWidth - box.right))}px`;
}

/** Who the open menu is for: null until somebody has been found signed in. */
let menuUser = null;

function buildMenu(user) {
  panel = document.createElement('div');
  panel.className = 'nav-account-menu';
  panel.id = 'nav-account-menu';
  panel.hidden = true;

  const who = document.createElement('p');
  who.className = 'nav-account-who';
  if (user) {
    const strong = document.createElement('strong');
    strong.textContent = labelOf(user);
    who.append('Signed in as', document.createElement('br'), strong);
  } else {
    who.append('Not signed in. An account keeps your games and your rating.');
  }

  const item = (tag, label, glyph, attrs = {}) => {
    const el = document.createElement(tag);
    el.className = 'nav-account-item';
    el.innerHTML = svg(ICONS[glyph] || '');
    const text = document.createElement('span');
    text.textContent = label;
    el.append(text);
    Object.assign(el, attrs);
    return el;
  };
  const rule = () => {
    const hr = document.createElement('hr');
    hr.className = 'nav-account-rule';
    return hr;
  };
  const signIn = user ? null : item('a', 'Sign in', 'profile', { href: opener.href });
  const profile = user ? item('a', 'Your profile', 'profile', { href: PROFILE.href }) : null;
  const games = user ? item('a', 'Your games', 'games', { href: MY_GAMES.href }) : null;
  const board = item('a', 'Leaderboard', 'board', { href: LEADERBOARD.href });
  const page = user?.username ? item('a', 'Your public page', 'page', { href: playerUrl(user.username) }) : null;
  const account = user ? item('a', 'Account', 'account', { href: opener.href }) : null;

  // The page's own View and theme buttons, pressed from here. Looked up each time
  // the menu opens rather than now: the View button is written in by the page's
  // script, which can finish after this one, and most pages have neither.
  const tools = document.createElement('div');
  tools.className = 'nav-account-tools';
  const view = item('button', 'View', 'view', { type: 'button', title: 'Everything the board is made of' });
  const count = document.createElement('span');
  count.className = 'nav-account-count';
  view.append(count);
  view.addEventListener('click', () => {
    closeMenu();
    document.getElementById('view-open')?.click();
  });
  const theme = item('button', 'Light mode', null, { type: 'button' });
  // Left open: the whole page changes round it, and a second press undoes it.
  theme.addEventListener('click', () => {
    document.getElementById('theme')?.click();
    syncTools();
  });
  // Only while the masthead's button is offering itself (it is hidden there once this menu exists).
  const install = item('button', 'Install the app', 'install', { type: 'button', title: 'Keep it on this device' });
  install.addEventListener('click', () => {
    closeMenu();
    document.getElementById('install')?.click();
  });
  tools.append(rule(), install, view, theme);
  panel.syncTools = syncTools;
  function syncTools() {
    const viewButton = document.getElementById('view-open');
    const themeButton = document.getElementById('theme');
    const installButton = document.getElementById('install');
    view.hidden = !viewButton;
    theme.hidden = !themeButton;
    install.hidden = !installButton || installButton.hidden;
    tools.hidden = view.hidden && theme.hidden && install.hidden;
    // How many settings have been changed, as the View button itself shows it.
    count.textContent = viewButton?.querySelector('.tab-n')?.textContent || '';
    // Named, and drawn, by the mode it switches TO, as the button was.
    const light = document.documentElement.dataset.theme === 'light';
    theme.querySelector('span').textContent = light ? 'Dark mode' : 'Light mode';
    theme.querySelector('svg').innerHTML = light ? MOON : SUN;
  }

  const signOut = item('button', 'Sign out', 'signOut', { type: 'button' });
  signOut.addEventListener('click', async () => {
    signOut.disabled = true;
    try {
      await fetch(LOGOUT, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
    } catch { /* offline: the reload below shows whatever is true */ }
    noteSignedIn(false);
    location.reload();
  });

  panel.append(who);
  if (user?.admin) panel.append(item('a', 'Admin', 'admin', { href: ADMIN.href, className: 'nav-account-item admin' }));
  panel.append(...[signIn, profile, page, games, board, account].filter(Boolean), tools);
  if (user) panel.append(rule(), signOut);
  document.body.append(panel);
}

/** Once: what closes the menu, and what keeps it under its button. */
function listen() {
  document.addEventListener('pointerdown', (event) => {
    if (panel && !panel.hidden && !panel.contains(event.target) && !event.target.closest('[data-account-link]')) closeMenu();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMenu({ focus: true });
  });
  // Kept under the button rather than closed: a phone scrolls a little on its own
  // when the address bar hides, and a menu that shut then would never stay open.
  const follow = () => { if (panel && !panel.hidden) place(); };
  addEventListener('resize', follow);
  addEventListener('scroll', follow, { passive: true, capture: true });
}

/**
 * Make the button open the menu, for `user` (null: signed out). Called again for
 * a user found signed in after the page is up: the menu built so far is thrown
 * away and the next press builds theirs.
 */
function menuOn(link, user) {
  menuUser = user;
  if (panel && panel.forUser !== user) { closeMenu(); panel.remove(); panel = null; }
  // `in`, not the value: the value is '', which is falsy, and a second listener
  // would shut the menu in the same click that opened it.
  if ('menu' in link.dataset) return;
  link.dataset.menu = '';
  link.setAttribute('aria-haspopup', 'menu');
  link.setAttribute('aria-expanded', 'false');
  link.setAttribute('aria-controls', 'nav-account-menu');
  link.addEventListener('click', (event) => {
    // A new tab or window still gets the account page.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const wasOpen = panel && !panel.hidden && opener === link;
    closeMenu();
    if (wasOpen) return;
    opener = link;
    if (!panel) { buildMenu(menuUser); panel.forUser = menuUser; }
    panel.syncTools();
    place();
    panel.hidden = false;
    link.setAttribute('aria-expanded', 'true');
    panel.querySelector('a, button')?.focus({ preventScroll: true });
  });
}

async function refresh() {
  const links = document.querySelectorAll('[data-account-link]');
  if (!links.length) return;
  // Straight away, so the buttons do not show and then vanish.
  takeTools(true);
  listen();
  links.forEach((link) => menuOn(link, null));
  let noted = null;
  try { noted = localStorage.getItem(KEY); } catch { /* blocked */ }
  if (!noted) return;
  try {
    const response = await fetch(ME, { credentials: 'same-origin' });
    if (!response.ok) return;
    const { user } = await response.json();
    if (!user) { noteSignedIn(false); return; }
    // The owner's own browsers stop counting themselves, the same switch the admin
    // page's "Don't count this browser" sets. Left on after signing out: a browser
    // that has been the owner's once is the owner's, and it is undone from there.
    if (user.admin) {
      try { localStorage.setItem(ANALYTICS_OFF_KEY, '1'); } catch { /* blocked */ }
    }
    settle(user);
    askForUsername(user);
    links.forEach((link) => {
      signedInFace(link, labelOf(user));
      menuOn(link, user);
    });
  } catch { /* offline: leave the button as it is, a menu for somebody signed out */ }
}

/**
 * Somebody signed in before usernames existed, or closed the account page
 * before choosing one: a line at the foot of the page asks, on every page, until
 * they do. The account page asks with a whole card, so it is left out there.
 * Dismissed for this tab only — the name is still needed, just not this minute.
 */
const ASKED_KEY = 'immortal-username-asked';
function askForUsername(user) {
  if (user.username || location.pathname.startsWith(new URL(ACCOUNT).pathname)) return;
  try { if (sessionStorage.getItem(ASKED_KEY)) return; } catch { /* blocked */ }
  const bar = document.createElement('div');
  bar.className = 'nav-username-ask';
  bar.setAttribute('role', 'status');
  const text = document.createElement('span');
  text.textContent = 'Choose the username you will be known by here.';
  const go = document.createElement('a');
  go.href = ACCOUNT.href;
  go.textContent = 'Choose a username';
  const later = document.createElement('button');
  later.type = 'button';
  later.textContent = 'Later';
  later.setAttribute('aria-label', 'Ask me later');
  later.addEventListener('click', () => {
    try { sessionStorage.setItem(ASKED_KEY, '1'); } catch { /* blocked */ }
    bar.remove();
  });
  bar.append(text, go, later);
  document.body.append(bar);
}

/**
 * Whether a board page's masthead has wrapped, measured with the button in its
 * natural place (last), and marked on the header for `nav.css` to act on.
 *
 * Only for a masthead with a title in `.who`: the library's is laid out by its
 * own rules. Watched with a ResizeObserver on the WIDTH alone, because marking
 * the header changes its height, and a height change would observe itself.
 */
function placeLogin(header) {
  const login = header.querySelector(':scope > .nav-login');
  const first = header.firstElementChild;
  if (!login || !first || !header.querySelector(':scope > .who')) return;
  let width = -1;
  const measure = () => {
    if (header.clientWidth === width) return;
    width = header.clientWidth;
    header.removeAttribute('data-login-wrap');
    const wrapped = login.getBoundingClientRect().top >= first.getBoundingClientRect().bottom;
    if (wrapped) header.setAttribute('data-login-wrap', '');
  };
  measure();
  new ResizeObserver(() => requestAnimationFrame(measure)).observe(header);
  // Late arrivals change what fits: Install appears when the browser decides the
  // site is installable, and the nav and the tools are written in by scripts.
  new MutationObserver(() => { width = -1; requestAnimationFrame(measure); })
    .observe(header, { childList: true, subtree: true });
}

document.querySelectorAll('header.top').forEach(placeLogin);
refresh().finally(() => settle(null));

/**
 * The masthead's height, as `--mast-h` on `<html>`, for what docks under it.
 *
 * `nav.css` holds the masthead to the top of every page, and a page that also
 * docks something of its own — the board in portrait, the transport bar on a
 * narrow landscape page — has to dock it BELOW the masthead, not behind it. The
 * masthead is not one height: it wraps to a second row on a narrow page and it
 * is drawn at the `--ui` scale, so it is measured rather than written down.
 * Real pixels, from `getBoundingClientRect`; a zoomed rule divides by `--ui`.
 */
const mast = document.querySelector('header.top');
if (mast) {
  const fit = () => document.documentElement.style.setProperty(
    '--mast-h', `${Math.round(mast.getBoundingClientRect().height)}px`);
  fit();
  new ResizeObserver(fit).observe(mast);
}
